/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What an expression yields** — declared, or derived where it is certain (`FD11` §4–§7, slice
 * `4e`, `#1415`).
 *
 * `Type` is a **declaration** (Todd, 2026-09-23): it says what the expression yields, and coercion is
 * what the runtime does when a resolved value disagrees and can be converted. So one word answers
 * two questions a row asks — *what does the driver coerce to* and *what does the checker compare* —
 * and this file is where both readers get the answer.
 *
 * ## Two layers, the row's and the expression's, and the row wins
 *
 * A row's `asType` is the **override**; an expression's `asType` is the **declaration**. Absence on
 * the row means *use the expression's*, never *there is none* — `X36`'s rule for `default`, one slot
 * along, and for the same reason: a shared expression is **addressed rather than copied** (`X5`), so
 * every consumer reads a declaration it cannot open, and the row is where a surface-specific answer
 * lives. Because the declaration sits **on the expression**, it travels by construction: reference
 * resolution substitutes the expression and the field comes with it, and so do a copy and a promote.
 *
 * ## `auto` states what is sent, and the runtime does what it states (`FD13`, `#1430`)
 *
 * Until `FD13` only a declaration coerced and a derivation only reported, so `auto (number)` over
 * `add(1, 2)` sent `"3"`, and a path to `playerWidth` sent `"1920"` into a number field. Todd,
 * 2026-09-24: *"What the destination needs is what wins, and if we can't coerce it it's a warning."*
 * So {@link sentType} is the one answer, read by the runtime, the `auto` label and the warning:
 *
 * 1. the row's own `Type` — the author answering;
 * 2. else the **destination's** declared single-value type — the side that rejects a request;
 * 3. else what the source yields, declared or derived;
 * 4. else nothing: the value goes as it resolved.
 *
 * Whether the source converts to the destination is {@link YIELD_CONVERSION}, one table. A value that
 * resolves and will not convert is omitted, leaving the driver's own value — the rule a failed `Type`
 * conversion already followed. A **structured** destination is never produced by converting a single
 * value (`FD11` §5), so there the runtime leaves the value as it resolved and the shape check reports.
 *
 * The derivation stays **generous** (`FD11` §3): it names a yield only where the shape makes it
 * certain. That it now reaches the wire does not conflict with `E1`: it decides a conversion the
 * runtime attempts and abandons when the value cannot take it, it never asserts that something works.
 *
 * | Shape | Derives |
 * | --- | --- |
 * | `computed` | the operation's own answer — arithmetic and `timestamp`/`rnd` a number, `concat`/`uuid` text |
 * | `conditional` | what every branch that produces a value agrees on; nothing if they disagree or any is unknown |
 * | `reference` | the target's declaration, else its derivation — through the caller's lookup |
 * | `static` | **nothing.** Stored as text, so `"12"` may be a number to whoever reads it; the declaration decides |
 * | `calculated` | **nothing.** What a path yields is the source declaration's question (`#1205`), and `kind` is superseded, not populated (`FD11` §2) |
 * | `suppress` | nothing — it produces no value |
 *
 * ## It reads expressions structurally
 *
 * So this file imports only `destination-field` — already aliased into the console under the
 * closed-subgraph rule — and importing `MacroExpression` would drag `PodDto` in with it
 * (`expression-default-rule.ts` carries the same constraint for the same reason).
 */

import { DESTINATION_TYPE_PHRASE, type DestinationField, type DestinationFieldType } from './destination-field';

/** The declaration's vocabulary, once — `DestinationFieldType`'s five, in the order the control offers them. */
export const EXPRESSION_YIELD_TYPES: readonly DestinationFieldType[] = [
  'string',
  'number',
  'boolean',
  'array',
  'object',
];

/**
 * **The operator's word for each member.** The stored member is the declaration's (`array`), and the
 * word an operator reads is the one `checkDestinationValue`'s sentences already use (*a list*) — so a
 * finding saying *expects a list* and a select offering `list` are one word on screen, which is what
 * `X3` asks of a surface. `array` stays on the model because the catalogues, the declaration and the
 * standards all spell it that way.
 */
export const EXPRESSION_YIELD_LABEL: Record<DestinationFieldType, string> = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
  array: 'list',
  object: 'object',
};

/** Is this one of the five members? A stored value from before the widening is still one of them. */
export function isExpressionYieldType(value: unknown): value is DestinationFieldType {
  return typeof value === 'string' && (EXPRESSION_YIELD_TYPES as readonly string[]).includes(value);
}

/** Does this type describe a structure rather than a single value? The axis the shape check compares on. */
export function isStructuredYield(type: DestinationFieldType): boolean {
  return type === 'array' || type === 'object';
}

/**
 * The member the runtime can **coerce** to, or `undefined`.
 *
 * A declared `array` or `object` is a statement about the value, not an instruction to convert it:
 * there is no defined parse from a resolved string to a list, and inventing one is a vocabulary
 * written to be un-written (`FD11` §5). So the runtime leaves such a value as it stands and the
 * authoring surface reports a disagreement instead.
 */
export function coercibleScalar(type: DestinationFieldType | undefined): 'string' | 'number' | 'boolean' | undefined {
  return type === 'string' || type === 'number' || type === 'boolean' ? type : undefined;
}

/**
 * **Convert resolved text to a single-value type**, or `undefined` where it cannot be one — the one
 * conversion the ORTB driver applies and the body's value check predicts (`FD13`). It was private to
 * the driver, so the console could only guess what a literal would become.
 *
 * Returns `undefined` when the coercion is impossible, which the caller turns into
 * an omitted key. That is the whole point of the setting: the Type or the field says
 * this must be a number, so shipping the string it could not convert is the one
 * answer that is definitely wrong — it is the type violation `$type` exists to
 * prevent, and it fails silently where an absent key is at least visible in the
 * inspector. Same rule as #880's unresolved macro.
 *
 * Boolean-ish strings coerce to `0`/`1` for `number` rather than to `NaN`. The
 * engine returns strings, and the natural sources for a numeric OpenRTB flag —
 * `gdpr`, `coppa`, `dnt` — are booleans in the session, so `Number('false')` was
 * `NaN` and the old default shipped the *word* `"false"` under `$type: 'number'`.
 * That is exactly the "Send as didn't send a number" report.
 *
 * **An empty value is absent, not zero, and not false (#940).** `Number('')` is `0`
 * and finite, so an empty resolved value used to ship as `0` — and the engine's
 * default *is* the empty string (`ortbBodyLeafDefault(node) ?? ''`), so any conditional
 * whose selected branch did not resolve asserted `regs.coppa: 0`: "COPPA does not
 * apply", stated from nothing. That is the same defect as sending an empty
 * `us_privacy`, one layer down. Absence must stay absent so the key is omitted.
 *
 * `string` deliberately still passes an empty value through: unlike a number or a
 * flag, an empty string *is* a legal value of its type, and the surfaces where a
 * blank has meaning (consent strings) suppress it themselves.
 */
export function convertScalar(
  value: string,
  type?: 'number' | 'boolean' | 'string',
): string | number | boolean | undefined {
  if (type === 'number') {
    const s = value.trim().toLowerCase();
    if (s === '') return undefined;
    if (s === 'true' || s === 'yes') return 1;
    if (s === 'false' || s === 'no') return 0;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  }
  if (type === 'boolean') {
    const s = value.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no') return false;
    return undefined;
  }
  return value;
}

/** Resolves a reference's id to the expression it names — `undefined` where it cannot be read. */
export type ExpressionYieldLookup = (ref: string) => unknown;

interface YieldNode {
  type?: unknown;
  asType?: unknown;
  operation?: unknown;
  then?: unknown;
  else?: unknown;
  ref?: unknown;
}

function asNode(expression: unknown): YieldNode | undefined {
  return typeof expression === 'object' && expression !== null ? (expression as YieldNode) : undefined;
}

/** What this expression **declares** it yields — its own `asType`, and nothing derived. */
export function declaredYield(expression: unknown): DestinationFieldType | undefined {
  const node = asNode(expression);
  return node && isExpressionYieldType(node.asType) ? node.asType : undefined;
}

/**
 * Bounds the walk through references. A cycle is reported by the resolver, not here; here it is only
 * a reason to stop and say nothing.
 */
const MAX_DEPTH = 8;

/**
 * **Every operation's yield — held to the vocabulary by a spec, not by the key type.**
 *
 * It is keyed by `string` because this module is aliased into the console, and `ComputedOperation`
 * lives in `macro-expression.dto.ts`, which imports the pod and demand-source DTOs the console's closed
 * import subgraph must not reach. A `Record<string, …>` cannot report a missing member, so
 * `expression-yield.spec.ts` compares these keys with `COMPUTED_OPERATIONS` in both directions — `2e`'s
 * `avg` is the member that would otherwise have yielded `undefined` with nobody told.
 */
export const OPERATION_YIELD: Readonly<Record<string, DestinationFieldType>> = {
  concat: 'string',
  uuid: 'string',
  add: 'number',
  subtract: 'number',
  multiply: 'number',
  divide: 'number',
  avg: 'number',
  timestamp: 'number',
  rnd: 'number',
};

/**
 * What this expression yields where its **shape** makes that certain — see the table above. Its own
 * declaration is not consulted; {@link expressionYield} puts the two together.
 */
export function derivedYield(
  expression: unknown,
  lookup: ExpressionYieldLookup | null,
  depth = 0,
): DestinationFieldType | undefined {
  const node = asNode(expression);
  if (!node || depth > MAX_DEPTH) return undefined;
  switch (node.type) {
    case 'computed':
      if (typeof node.operation !== 'string') return OPERATION_YIELD.concat;
      // An operation nobody declares yields nothing certain, as it did when this was keyed by string.
      return Object.prototype.hasOwnProperty.call(OPERATION_YIELD, node.operation)
        ? OPERATION_YIELD[node.operation]
        : undefined;
    case 'conditional': {
      const branches = [node.then, node.else].filter((branch) => asNode(branch)?.type !== 'suppress');
      // A missing `else` resolves to nothing, which is not a value of any type — the branches that
      // do produce one are what the answer is about.
      const present = branches.filter((branch) => branch !== undefined);
      if (present.length === 0) return undefined;
      const yields = present.map((branch) => expressionYield(branch, lookup, depth + 1));
      const first = yields[0];
      return first !== undefined && yields.every((type) => type === first) ? first : undefined;
    }
    case 'reference': {
      if (!lookup || typeof node.ref !== 'string') return undefined;
      return expressionYield(lookup(node.ref), lookup, depth + 1);
    }
    default:
      return undefined;
  }
}

/** The expression's declaration where it makes one, otherwise what its shape derives. */
export function expressionYield(
  expression: unknown,
  lookup: ExpressionYieldLookup | null,
  depth = 0,
): DestinationFieldType | undefined {
  return declaredYield(expression) ?? derivedYield(expression, lookup, depth);
}

/**
 * **What a row's value will be, as far as anything declares** — the row's own override, else the
 * expression's declaration, else what its shape derives.
 *
 * `source` says which layer answered, because the control has to render the difference: an inherited
 * declaration reads *from &lt;name&gt;*, a derivation reads `auto (…)`, and a row's own answer reads as
 * itself (*state what is in force, or say you do not know*).
 */
export function rowYield(
  rowAsType: unknown,
  expression: unknown,
  lookup: ExpressionYieldLookup | null,
): { type: DestinationFieldType; source: 'row' | 'declared' | 'derived' } | undefined {
  if (isExpressionYieldType(rowAsType)) return { type: rowAsType, source: 'row' };
  const node = asNode(expression);
  // A reference's declaration is the **target's**, read through the lookup — the reference node
  // itself never carries one, because the row beside it is where an override lives.
  const target = node?.type === 'reference' && lookup && typeof node.ref === 'string' ? lookup(node.ref) : expression;
  const declared = declaredYield(target);
  if (declared) return { type: declared, source: 'declared' };
  const derived = derivedYield(expression, lookup);
  return derived ? { type: derived, source: 'derived' } : undefined;
}

/**
 * **Does a value of one type convert to another?** (`FD13`) — one table, read by the runtime, the
 * `auto` label and the shape warning, so the three cannot disagree.
 *
 * - `always` — every value converts: anything becomes text, and `true`/`false` become `1`/`0`.
 * - `value` — some values do, and which is only known at request time: `"12"` is a number and `"12a"`
 *   is not. Not a finding: the runtime converts where it can and omits where it cannot.
 * - `never` — a single value does not become a list or an object, and a structure does not become a
 *   single value by conversion (`FD11` §5). This is the case the author is warned about.
 */
export type YieldConversion = 'always' | 'value' | 'never';

export const YIELD_CONVERSION: Readonly<
  Record<DestinationFieldType, Readonly<Record<DestinationFieldType, YieldConversion>>>
> = {
  string: { string: 'always', number: 'value', boolean: 'value', array: 'never', object: 'never' },
  number: { string: 'always', number: 'always', boolean: 'value', array: 'never', object: 'never' },
  boolean: { string: 'always', number: 'always', boolean: 'always', array: 'never', object: 'never' },
  array: { string: 'never', number: 'never', boolean: 'never', array: 'always', object: 'never' },
  object: { string: 'never', number: 'never', boolean: 'never', array: 'never', object: 'always' },
};

/**
 * **What a row sends, and who decided it** (`FD13`) — see the file header for the order.
 *
 * `source` is who answered: `row` (its own `Type`), `destination` (the slot's declaration),
 * `declared` (the expression's `asType`), `derived` (its shape). `yields` is what the source side
 * produces where anything says so, and `converts` is {@link YIELD_CONVERSION}'s answer from it to
 * `type` — `undefined` where the source is unknown or the row decided.
 *
 * `destination` is **required and nullable**: `null` is a surface whose runtime does not convert to
 * a declared destination, or a slot that declares none. An optional argument here would be the
 * `#1254` door, a reader that silently answers with less.
 */
export function sentType(
  rowAsType: unknown,
  expression: unknown,
  destination: DestinationFieldType | null,
  lookup: ExpressionYieldLookup | null,
):
  | {
      type: DestinationFieldType;
      source: 'row' | 'destination' | 'declared' | 'derived';
      yields: DestinationFieldType | undefined;
      converts: YieldConversion | undefined;
    }
  | undefined {
  if (isExpressionYieldType(rowAsType))
    return { type: rowAsType, source: 'row', yields: undefined, converts: undefined };
  const fromSource = rowYield(undefined, expression, lookup);
  const yields = fromSource?.type;
  if (destination) {
    return {
      type: destination,
      source: 'destination',
      yields,
      converts: yields ? YIELD_CONVERSION[yields][destination] : undefined,
    };
  }
  if (!fromSource || fromSource.source === 'row') return undefined;
  return { type: fromSource.type, source: fromSource.source, yields, converts: 'always' };
}

/**
 * **The type the runtime coerces a row's resolved value to** — {@link sentType}'s answer, where it is
 * a single value. A structured answer is a statement about the value, not an instruction to convert
 * it (`FD11` §5), so the runtime leaves such a value as it resolved.
 *
 * Read at the point each driver already coerces, after references have been resolved away at the
 * config read boundary, so the expression here is the target itself and carries its own `asType` —
 * which is why no lookup is taken.
 */
export function runtimeCoercion(
  rowAsType: unknown,
  expression: unknown,
  destination: DestinationFieldType | null,
): 'string' | 'number' | 'boolean' | undefined {
  return coercibleScalar(sentType(rowAsType, expression, destination, null)?.type);
}

/** Where {@link rowYield}'s answer came from, as the shape finding phrases it. */
function yieldSubject(inForce: NonNullable<ReturnType<typeof rowYield>>, from: string | null): string {
  const phrase = DESTINATION_TYPE_PHRASE[inForce.type];
  if (inForce.source === 'row') return `this row's Type says it is ${phrase}`;
  if (inForce.source === 'declared') return `${from ? `"${from}"` : 'the expression'} declares ${phrase}`;
  return `this value produces ${phrase}`;
}

/**
 * **Shape against shape** — a list or an object where the slot takes a single value, or the reverse
 * (`#1413` piece 3, `FD11` §7c, slice `4e`).
 *
 * Generous, as `4d`'s concept check is (`FD11` §3): it reports only where **both ends** say what they
 * are — the slot's declared type, and the row's `Type` or the expression's declaration or a shape
 * that makes it certain. An undeclared yield is silence. And it compares **shapes**, not scalars: a
 * number into a text field is what `Type` and the driver's scalar coercion already answer, so warning
 * about it would be a surface warning about correct behaviour.
 *
 * **Its redirect names only what is reachable here** (`#1272`, *shipped somewhere is not reachable
 * here*). Two ways out exist, and each is offered only where it works:
 *
 * - **one element of it** — where the value is a path, because the path already reads
 *   `genres[0]` (`splitAuthoredPath`, and `4d` made a member of a list still name its concept), and
 *   for an object **one of its members**, which the path reaches by name;
 * - **Send structured values as** — only where the row offers that control. On the ORTB body that
 *   is where a list could reach a field that takes one value (`4f`, `bodyLeafRendering`).
 *
 * **A row that has authored a rendering has answered, and is not warned** (`4f`). The finding asks the
 * operator to decide how the value goes out; once they have, repeating it is a surface warning about
 * correct behaviour. `rendered` is required, so each caller states it.
 *
 * A `warn`, like `4d`'s: nothing blocks a save.
 */
export function checkYieldAgainstDestination(
  field: Pick<DestinationField, 'name' | 'valueType'> | undefined,
  inForce: ReturnType<typeof rowYield>,
  context: {
    from: string | null;
    path: string | null;
    offersRendering: boolean;
    rendered: boolean;
    /**
     * **Whether the surface carries text or structure** (`#1458`, Todd 2026-09-28). On a text surface a
     * list into a single-value field is not a finding: it goes out comma-joined, the convention a
     * query string has always used, so it converts *always* there and only an object warns. Required,
     * so a caller cannot forget which surface it is.
     */
    textSurface: boolean;
  },
): { kind: 'mismatch'; message: string } | null {
  if (!field?.valueType || !inForce || context.rendered) return null;
  if (context.textSurface && inForce.type === 'array' && !isStructuredYield(field.valueType)) return null;
  const expected = field.valueType;
  const actual = inForce.type;
  // The one table (`FD13`): two single values always convert or convert by value, which is the
  // runtime's business, so only a conversion that can never happen is a finding.
  if (YIELD_CONVERSION[actual][expected] !== 'never') return null;
  const opening = `"${field.name}" takes ${DESTINATION_TYPE_PHRASE[expected]}, and ${yieldSubject(inForce, context.from)}.`;
  if (isStructuredYield(expected)) return { kind: 'mismatch', message: opening };

  const ways: string[] = [];
  const path = context.path?.trim();
  // **The way out follows the shape** (`#1458`): `[0]` is an element of a list and names nothing in
  // an object, where the path reaches a member by name.
  if (path) {
    ways.push(
      actual === 'object'
        ? `map one of its members instead — "${path}.<member>"`
        : `map one element of it instead — "${path}[0]"`,
    );
  }
  if (context.offersRendering) ways.push('choose how it is sent under Send structured values as');
  const redirect = ways.length > 0 ? ` To send ${DESTINATION_TYPE_PHRASE[expected]}, ${ways.join(', or ')}.` : '';
  return { kind: 'mismatch', message: `${opening}${redirect}` };
}
